import { RootState } from '@Common/models/redux/store';
import {
  ClickAwayListener,
  FormControl,
  InputAdornment,
  OutlinedInput,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import { Search as SearchIcon } from '@mui/icons-material';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import citationGeneratorTrack from '../handlers/track';
import { useCitationSearchQuery } from './CitaionSearchBar';
import { CitationGeneratorXPath } from '../constants/xpath.constants';

const useStyles = makeStyles((theme) => ({
  form: {
    '&.collapsed': {
      marginRight: 20,
    },
  },
  root: {
    paddingLeft: 5,
    transition: 'all 0.8s',
    width: 'auto',
    '&.collapsed': {
      width: 0,
    },
  },
  input: {
    padding: '5px 5px 5px 0',
    fontSize: 11,
  },
  notchedOutline: {
    transition: 'all 0.3s',
    borderRadius: 2,
    '&.collapsed': {
      display: 'none',
    },
  },
  inputAdornmentPositionStart: {
    marginRight: 5,
    cursor: 'pointer',
    '&.isDisabled': {
      pointerEvents: 'none',
    },
    '&:hover $searchIcon': {
      color: theme.palette.primary.main,
    },
  },
  searchIcon: {
    color: theme.specialColors.ICON_DEFAULT_COLOR,
    '&.isDisabled': {
      pointerEvents: 'none',
      opacity: 0.3,
    },
  },
}));

export default function CitationSearchIcon() {
  const { query, setQuery } = useCitationSearchQuery('');
  const [isCollapsed, setIsCollapsed] = useState(true);
  const classes = useStyles();
  const router = useRouter();
  const isCowriter = router.pathname.includes('/co-writer');
  const citationList = useSelector((state: RootState) => state.ctgr.citationList);
  const citationsFromCowriter = useSelector((state: RootState) => state.ctgr.citationsFromCowriter);
  const citations = isCowriter ? citationsFromCowriter : citationList;

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (!query) {
          setIsCollapsed(true);
        }
      }}
    >
      <FormControl className={clsx(classes.form, { collapsed: isCollapsed })}>
        <OutlinedInput
          placeholder="Search"
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            citationGeneratorTrack.userSearchedInCitationList();
          }}
          classes={{
            root: clsx(classes.root, { collapsed: isCollapsed }),
            input: classes.input,
            notchedOutline: clsx(classes.notchedOutline, { collapsed: isCollapsed }),
          }}
          startAdornment={
            <InputAdornment
              position="start"
              className={clsx(classes.inputAdornmentPositionStart, {
                isDisabled: citations.length === 0,
              })}
              onClick={() => {
                if (isCollapsed) {
                  setIsCollapsed(false);
                }
              }}
            >
              <SearchIcon
                className={clsx(classes.searchIcon, {
                  isDisabled: citations.length === 0,
                })}
                fontSize="small"
              />
            </InputAdornment>
          }
        />
      </FormControl>
    </ClickAwayListener>
  );
}
